import React, { useEffect, useState } from 'react';
import { partnerApiService } from '../../services/api';
import {
  ReportedReview,
  ReviewReportStatus,
  REVIEW_REPORT_REASON_LABELS,
  REVIEW_REPORT_STATUS_LABELS,
} from '../../types/api';
import { withCount } from '../../utils/plural';
import { Stars } from './ReviewsPanel';

// Цвета исходов повторяют мобильный кабинет, чтобы партнёр видел одну и ту же
// жалобу одинаково в двух местах: жёлтый — ждёт модератора, красный — жалоба
// подтверждена и отзыв снят, зелёный — отклонена, отзыв оставлен.
const STATUS_CLASS: Record<ReviewReportStatus, string> = {
  pending: 'qp-status qp-status--warn',
  resolved: 'qp-status qp-status--bad',
  dismissed: 'qp-status qp-status--ok',
};

/** «29.08.2026» — формат дат жалоб, как в мобильном кабинете. */
const formatDate = (value: string): string => new Date(value).toLocaleDateString('ru-RU');

/**
 * Жалобы гостей на отзывы заведения.
 *
 * Витрина сознательно read-only: решение по жалобе принимает модератор,
 * партнёр не может ни отклонить её, ни снять отзыв. Автор жалобы не
 * показывается ни в каком виде — бэк его и не отдаёт: имя жалобщика было бы
 * только поводом свести счёты с гостем.
 *
 * Секция живёт на главной сразу под «Последними отзывами»: жалобы — вторая
 * половина того же разговора об отзывах.
 */
export const ReviewReportsPanel: React.FC = () => {
  const [reviews, setReviews] = useState<ReportedReview[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    partnerApiService
      .getReviewReports()
      .then((data) => {
        if (!cancelled) setReviews(data);
      })
      .catch((error) => {
        console.error('Ошибка загрузки жалоб на отзывы:', error);
        if (!cancelled) setLoadError('Не удалось загрузить жалобы на отзывы');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="qp-panel">
      <header className="qp-panel-head">
        <div>
          <h2 className="qp-label">Жалобы на отзывы</h2>
          <p className="qp-card-sub" style={{ marginTop: 4 }}>
            Разбирает модератор в течение 24 часов. Кто пожаловался — не раскрывается,
            отзыв остаётся на месте, пока решение не принято.
          </p>
        </div>
        {!isLoading && !loadError && reviews.length > 0 && (
          <span className="qp-strip-text">{reviews.length}</span>
        )}
      </header>

      {loadError ? (
        <div className="qp-notice qp-notice--error">{loadError}</div>
      ) : isLoading ? (
        <div className="qp-skel" style={{ height: 120 }} />
      ) : reviews.length === 0 ? (
        <div className="qp-empty">
          <h3>Жалоб нет</h3>
          <p>Ни на один отзыв о вашем заведении гости не жаловались.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {reviews.map((review) => (
            <ReportedReviewCard key={review.id} review={review} />
          ))}
        </div>
      )}
    </section>
  );
};

/** Карточка отзыва с жалобами: сам отзыв как в панели отзывов, ниже — жалобы. */
const ReportedReviewCard: React.FC<{ review: ReportedReview }> = ({ review }) => {
  const isHidden = review.hidden_at !== null;
  // «Снова опубликован» — только когда жалобу подтвердили (resolved), а отзыв
  // при этом виден: значит, модератор снял его и потом вернул. Без этой строки
  // партнёр не отличил бы возвращённый отзыв от так и не снятого.
  const isRepublished = !isHidden && review.reports.some((report) => report.status === 'resolved');
  const text = review.text?.trim();

  return (
    <article
      style={{
        border: '1px solid var(--qp2-line-soft)',
        borderRadius: 11,
        padding: 14,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
        <span className="qp-avatar" style={{ width: 28, height: 28, fontSize: 10.5 }}>
          {review.user?.avatar ? (
            <img
              src={review.user.avatar}
              alt=""
              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
            />
          ) : (
            review.user?.name?.charAt(0)?.toUpperCase() || 'Г'
          )}
        </span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{review.user?.name || 'Гость'}</div>
          <Stars rating={review.rating} />
        </div>
        {isHidden && (
          <span
            className="qp-status qp-status--bad"
            title={`Отзыв скрыт модератором ${formatDate(review.hidden_at!)}`}
          >
            Скрыт модерацией
          </span>
        )}
        <span className="qp-check-hint">
          {new Date(review.created_at).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}
        </span>
      </div>

      {text && (
        <p
          style={{
            margin: 0,
            fontSize: 13.5,
            lineHeight: 1.55,
            color: 'var(--qp2-muted)',
            // Снятый отзыв читаем, но приглушён: клиенты его уже не видят.
            opacity: isHidden ? 0.6 : 1,
          }}
        >
          {text}
        </p>
      )}

      <div
        style={{
          marginTop: 12,
          padding: '10px 13px',
          borderRadius: 10,
          background: 'var(--qp2-rose-soft)',
          color: 'var(--qp2-rose-ink)',
          borderLeft: '3px solid var(--qp2-rose)',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <span className="qp-label" style={{ color: 'inherit', opacity: 0.75 }}>
          {withCount(review.reports.length, 'жалоба', 'жалобы', 'жалоб')} на этот отзыв
        </span>

        {review.reports.map((report) => {
          const comment = report.comment?.trim();
          return (
            <div key={report.id}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>
                  {REVIEW_REPORT_REASON_LABELS[report.reason]}
                </span>
                <span className={STATUS_CLASS[report.status]}>
                  {REVIEW_REPORT_STATUS_LABELS[report.status]}
                </span>
                <span className="qp-check-hint" style={{ marginLeft: 'auto' }}>
                  {formatDate(report.created_at)}
                </span>
              </div>
              {comment && (
                <p style={{ margin: '4px 0 0', fontSize: 12.5, fontStyle: 'italic', lineHeight: 1.5 }}>
                  «{comment}»
                </p>
              )}
            </div>
          );
        })}

        {isRepublished && (
          <span style={{ fontSize: 12, fontWeight: 600 }}>Отзыв снова опубликован</span>
        )}
      </div>
    </article>
  );
};
